import {
  hashInterfacePrincipal,
  parseConfiguredInterfacePrincipal,
  type RuntimeInterfacePrincipalState,
} from "@brains/contracts";
import { z } from "@brains/utils/zod";
import { matchSpaceSelector } from "./space-selector";

/**
 * User permission level schema
 */
export const UserPermissionLevelSchema: z.ZodEnum<{
  admin: "admin";
  trusted: "trusted";
  public: "public";
}> = z.enum(["admin", "trusted", "public"]);

export type UserPermissionLevel = z.output<typeof UserPermissionLevelSchema>;

// Add new actions only when a concrete mutating tool needs them.
export const EntityActionSchema: z.ZodEnum<{
  create: "create";
  update: "update";
  delete: "delete";
  extract: "extract";
  publish: "publish";
}> = z.enum(["create", "update", "delete", "extract", "publish"]);

export type EntityAction = z.output<typeof EntityActionSchema>;

/**
 * Required level for an entity action.
 *
 * `never` forbids the action through system tools regardless of caller level.
 */
export const EntityActionRequiredLevelSchema: z.ZodEnum<{
  never: "never";
  admin: "admin";
  trusted: "trusted";
  public: "public";
}> = z.enum(["never", "admin", "trusted", "public"]);

export type EntityActionRequiredLevel = z.output<
  typeof EntityActionRequiredLevelSchema
>;

export const entityActionPolicyRuleSchema: z.ZodObject<
  {
    create: z.ZodOptional<typeof EntityActionRequiredLevelSchema>;
    update: z.ZodOptional<typeof EntityActionRequiredLevelSchema>;
    delete: z.ZodOptional<typeof EntityActionRequiredLevelSchema>;
    extract: z.ZodOptional<typeof EntityActionRequiredLevelSchema>;
    publish: z.ZodOptional<typeof EntityActionRequiredLevelSchema>;
  },
  z.core.$strict
> = z.strictObject({
  create: EntityActionRequiredLevelSchema.optional(),
  update: EntityActionRequiredLevelSchema.optional(),
  delete: EntityActionRequiredLevelSchema.optional(),
  extract: EntityActionRequiredLevelSchema.optional(),
  publish: EntityActionRequiredLevelSchema.optional(),
});

export type EntityActionPolicyRule = z.output<
  typeof entityActionPolicyRuleSchema
>;
export type EntityActionPolicyRuleInput = z.input<
  typeof entityActionPolicyRuleSchema
>;

export const entityActionPolicyConfigSchema: z.ZodRecord<
  z.ZodString,
  typeof entityActionPolicyRuleSchema
> = z.record(z.string(), entityActionPolicyRuleSchema);

export type EntityActionPolicyConfig = z.output<
  typeof entityActionPolicyConfigSchema
>;
export type EntityActionPolicyConfigInput = z.input<
  typeof entityActionPolicyConfigSchema
>;
export type EntityActionPolicyEntry = EntityActionPolicyRule;
export type EntityActionPolicy = EntityActionPolicyConfig;

const ACTION_LABELS: Record<EntityAction, string> = {
  create: "Creating",
  update: "Updating",
  delete: "Deleting",
  extract: "Extracting",
  publish: "Publishing",
};

const LEVEL_LABELS: Record<UserPermissionLevel, string> = {
  public: "Public",
  trusted: "Trusted",
  admin: "Admin",
};

/**
 * Generic interface for items with visibility
 */
export interface WithVisibility {
  visibility?: UserPermissionLevel;
}

/**
 * Permission rule for pattern-based permission matching
 */
export interface PermissionRule {
  pattern: string;
  level: UserPermissionLevel;
}

export type PermissionRuleInput = PermissionRule;

/**
 * Configuration for the permission system
 */
export interface ConfiguredPrincipalSeeds {
  admins: string[];
  anchors: string[];
  trusted: string[];
}

export interface PermissionConfig {
  /** Callers with administrative permission. */
  admins?: string[];
  /** Caller identities that represent the brain's person anchor in chat. */
  anchors?: string[];
  trusted?: string[];
  rules?: PermissionRuleInput[];
  entityActions?: EntityActionPolicyConfigInput;
}

export class EntityActionPermissionError extends Error {
  public readonly entityType: string;
  public readonly action: EntityAction;
  public readonly callerLevel: UserPermissionLevel;
  public readonly requiredLevel: UserPermissionLevel;

  constructor(input: {
    entityType: string;
    action: EntityAction;
    callerLevel: UserPermissionLevel;
    requiredLevel: UserPermissionLevel;
  }) {
    super(
      `${ACTION_LABELS[input.action]} \`${input.entityType}\` requires ${LEVEL_LABELS[input.requiredLevel]} permission; your current permission is ${LEVEL_LABELS[input.callerLevel]}.`,
    );
    this.name = "EntityActionPermissionError";
    this.entityType = input.entityType;
    this.action = input.action;
    this.callerLevel = input.callerLevel;
    this.requiredLevel = input.requiredLevel;
  }
}

/**
 * Additional context interfaces can provide for shared-space permission checks.
 *
 * Each interface normalizes its location concept (Discord channel/thread,
 * Slack channel, Matrix room, ...) into the single canonical `channelId`
 * before calling the resolver.
 */
export interface PermissionLookupContext {
  channelId?: string;
  isBot?: boolean;
  isGuest?: boolean;
}

interface SharedSpaceContext extends PermissionLookupContext {
  interfaceType: string;
}

export interface PermissionServiceOptions {
  /** Shared conversation space selectors, e.g. discord:123 or discord:project-* */
  spaces?: string[];
}

/**
 * Centralized permission service for determining user permission levels
 * Handles both explicit user lists and pattern-based rules
 * Replaces the previous permission handler.
 */
export class PermissionService {
  private admins: Set<string>;
  private anchors: Set<string>;
  private trusted: Set<string>;
  private readonly configuredPrincipalSeeds: ConfiguredPrincipalSeeds;
  private rules: PermissionRule[];
  private spaces: string[];
  private entityActions?: EntityActionPolicyConfig;

  constructor(
    config: PermissionConfig,
    options: PermissionServiceOptions = {},
  ) {
    this.configuredPrincipalSeeds = {
      admins: [...(config.admins ?? [])],
      anchors: [...(config.anchors ?? [])],
      trusted: [...(config.trusted ?? [])],
    };
    this.admins = configuredPrincipalKeys(this.configuredPrincipalSeeds.admins);
    this.anchors = configuredPrincipalKeys(
      this.configuredPrincipalSeeds.anchors,
    );
    this.trusted = configuredPrincipalKeys(
      this.configuredPrincipalSeeds.trusted,
    );
    this.rules = config.rules ?? [];
    this.spaces = options.spaces ?? [];
    if (config.entityActions) {
      this.entityActions = entityActionPolicyConfigSchema.parse(
        config.entityActions,
      );
    }
  }

  /**
   * Determine the permission level for a user in a specific interface.
   *
   * Resolution order (load-bearing — locks user-facing trust semantics):
   *   1. Explicit admin/trusted lists win — they encode operator intent.
   *   2. Pattern rules granting admin/trusted win — same intent, broader.
   *   3. Configured shared-space membership grants `trusted` — only raises
   *      otherwise-public callers; never elevates to `admin`.
   *   4. Public-fallback pattern rules apply.
   *   5. Default `public`.
   */
  determineUserLevel(
    interfaceType: string,
    userId: string,
    context: PermissionLookupContext = {},
  ): UserPermissionLevel {
    const fullId = `${interfaceType}:${userId}`;
    const principalKey = runtimePrincipalKey(
      interfaceType,
      hashInterfacePrincipal(interfaceType, userId),
    );

    if (this.admins.has(principalKey)) return "admin";
    if (this.trusted.has(principalKey)) return "trusted";

    const patternLevel = this.getPatternLevel(fullId);
    if (patternLevel === "admin" || patternLevel === "trusted") {
      return patternLevel;
    }

    if (
      this.spaces.length > 0 &&
      this.matchesConfiguredSpace({ interfaceType, ...context })
    ) {
      return "trusted";
    }

    return patternLevel ?? "public";
  }

  /** Whether the caller is this professional brain's Anchor identity. */
  isAnchor(interfaceType: string, userId: string): boolean {
    return this.anchors.has(
      runtimePrincipalKey(
        interfaceType,
        hashInterfacePrincipal(interfaceType, userId),
      ),
    );
  }

  getConfiguredPrincipalSeeds(): ConfiguredPrincipalSeeds {
    return {
      admins: [...this.configuredPrincipalSeeds.admins],
      anchors: [...this.configuredPrincipalSeeds.anchors],
      trusted: [...this.configuredPrincipalSeeds.trusted],
    };
  }

  replaceRuntimePrincipalState(state: RuntimeInterfacePrincipalState): void {
    this.admins = new Set(
      state.grants
        .filter((grant) => grant.permissionLevel === "admin")
        .map((grant) =>
          runtimePrincipalKey(grant.interfaceType, grant.principalKeyHash),
        ),
    );
    this.trusted = new Set(
      state.grants
        .filter((grant) => grant.permissionLevel === "trusted")
        .map((grant) =>
          runtimePrincipalKey(grant.interfaceType, grant.principalKeyHash),
        ),
    );
    this.anchors = new Set(
      state.anchors.map((anchor) =>
        runtimePrincipalKey(anchor.interfaceType, anchor.principalKeyHash),
      ),
    );
  }

  /**
   * Return the merged entity action policy for an entity type.
   * Entity-specific entries override the "*" default one action at a time.
   */
  getResolvedEntityActionPolicy(
    entityType: string,
  ): EntityActionPolicyRule | undefined {
    if (!this.entityActions) return undefined;

    const policy = {
      ...(this.entityActions["*"] ?? {}),
      ...(this.entityActions[entityType] ?? {}),
    };

    return Object.keys(policy).length > 0 ? policy : undefined;
  }

  getEntityActionRequiredLevel(
    entityType: string,
    action: EntityAction,
  ): EntityActionRequiredLevel | undefined {
    return this.getResolvedEntityActionPolicy(entityType)?.[action];
  }

  getRequiredEntityActionLevel(
    entityType: string,
    action: EntityAction,
  ): EntityActionRequiredLevel | undefined {
    return this.getEntityActionRequiredLevel(entityType, action);
  }

  canPerformEntityAction(
    userLevel: UserPermissionLevel | undefined,
    entityType: string,
    action: EntityAction,
  ): boolean {
    const requiredLevel = this.getEntityActionRequiredLevel(entityType, action);
    if (!requiredLevel) return true;
    if (requiredLevel === "never") return false;
    return this.hasPermission(userLevel ?? "public", requiredLevel);
  }

  assertEntityActionAllowed(
    entityType: string,
    action: EntityAction,
    userLevel: UserPermissionLevel | undefined,
  ): void {
    const requiredLevel = this.getEntityActionRequiredLevel(entityType, action);
    if (!requiredLevel) return;
    if (requiredLevel === "never") {
      throw new Error(
        `${ACTION_LABELS[action]} \`${entityType}\` is not allowed through system tools.`,
      );
    }

    const callerLevel = userLevel ?? "public";
    if (this.hasPermission(callerLevel, requiredLevel)) return;

    throw new EntityActionPermissionError({
      entityType,
      action,
      callerLevel,
      requiredLevel,
    });
  }

  /**
   * Check if a user has a specific permission level or higher
   * @param userLevel The user's actual permission level
   * @param requiredLevel The required permission level
   * @returns True if the user meets the permission requirement
   */
  hasPermission(
    userLevel: UserPermissionLevel,
    requiredLevel: UserPermissionLevel,
  ): boolean {
    return PermissionService.hasPermission(userLevel, requiredLevel);
  }

  /**
   * Filter items based on user permission level
   * Works for tools, commands, or any item with a visibility property
   * @param items Array of items with visibility levels
   * @param userLevel The user's permission level
   * @returns Filtered array of items the user can access
   */
  filterByPermission<T extends WithVisibility>(
    items: T[],
    userLevel: UserPermissionLevel,
  ): T[] {
    return items.filter((item) => {
      const requiredLevel = item.visibility ?? "public";
      return this.hasPermission(userLevel, requiredLevel);
    });
  }

  /**
   * Static method to check if a permission level meets the required level
   * Used by Shell for interface-based permission checking (commands, templates, etc.)
   * Maintains compatibility with existing Shell.generateContent() usage
   */
  static hasPermission(
    grantedLevel: UserPermissionLevel,
    requiredLevel: UserPermissionLevel,
  ): boolean {
    if (requiredLevel === "public") {
      return true; // Everyone has public access
    }

    if (requiredLevel === "trusted") {
      return grantedLevel === "trusted" || grantedLevel === "admin";
    }

    // At this point, requiredLevel must be "admin".
    return grantedLevel === "admin";
  }

  private getPatternLevel(id: string): UserPermissionLevel | undefined {
    for (const rule of this.rules) {
      if (matchSpaceSelector(rule.pattern, id)) {
        return rule.level;
      }
    }
    return undefined;
  }

  private matchesConfiguredSpace(context: SharedSpaceContext): boolean {
    if (context.isBot || context.isGuest) return false;
    if (!context.channelId) return false;

    const spaceId = `${context.interfaceType}:${context.channelId}`;
    return this.spaces.some((selector) =>
      matchSpaceSelector(selector, spaceId),
    );
  }
}

function configuredPrincipalKeys(values: string[]): Set<string> {
  return new Set(
    values.map((value) => {
      const principal = parseConfiguredInterfacePrincipal(value);
      return runtimePrincipalKey(
        principal.interfaceType,
        hashInterfacePrincipal(principal.interfaceType, principal.subject),
      );
    }),
  );
}

function runtimePrincipalKey(
  interfaceType: string,
  principalKeyHash: string,
): string {
  return `${interfaceType.trim().toLowerCase()}:${principalKeyHash}`;
}
